// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { ActionIcon, Menu, Text } from '@mantine/core';
import { useLingui } from '@lingui/react';
import { useModals } from '@mantine/modals';
import {
  IconCalendarPlus,
  IconCash,
  IconCheck,
  IconCopy,
  IconDots,
  IconDownload,
  IconPencil,
  IconSend,
  IconTrash,
  IconX,
} from '@tabler/icons';
import { FC, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EventParticipationsContext } from '../../../Service/Context/EventParticipationContextV2';
import { downloadDocumentContractQuestionnaire } from '../../../Service/DocumentService';
import { BookitupDocumentType } from '../../../Types/BookitupDocument';
import { useConfirmModalState } from '../../Dialogs';
import ChangeLabel from '../../Dialogs/ChangeLabel';
import { TestS } from '../../../TestS';
import { ToastS } from '../../../Service/ToastS';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { DocumentS } from '../../../Templates/Documents/DocumentS';
import { LicenceS } from '../../../Service/LicenceS';
import { DocumentDto } from '../../../Service/DocumentT';
import { openDeleteConfirmModal } from '../../../Atoms/DeleteConfirmModal';
import { DocumentItemContext } from '../../../Service/Context/DocumentItemContext';
import CreateDocumentModal, { openCreateDocumentModal } from './CreateDocumentModal';
import CreateDownPaymentModal from './CreateDownPaymentModal';
import PaymentsManagerModal from './payments/PaymentsModal';
import { openAssignEventModal, openCreateContractModal } from './DocumentModals';

const DocumentItemActions: FC<{
  bookitupDocument: DocumentDto;
  handleChange: (val: any) => void;
  reload: () => void;
  refresh: () => void;
  setDraft: (val: boolean) => void;
  patchDocument: (val: Partial<DocumentDto>, onSuccess?: () => void) => void;
}> = ({ bookitupDocument, handleChange, reload, refresh, setDraft, patchDocument }) => {
  const { openLicenceModal, executeWithLicenceCheck } = useContext(ViolationContext);
  const eventParticipantsContext = useContext(EventParticipationsContext);
  const { interceptViolation } = useContext(ViolationContext);
  const [, setLoading] = useState(false);
  const { showConfirm, toggleConfirm } = useConfirmModalState(false);
  const { syncedAt, isUptodate, toolMissing, sync } = useContext(DocumentItemContext);
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { openModal } = useModals();
  const {
    id,
    label,
    number: documentNumber,
    customerId,
    documentType,
    eventId,
    draft,
    finalInvoiceCreatable,
    acceptedAt,
    originDocumentId,
    paid,
  } = bookitupDocument;

  const deleteHandler = () => {
    if (bookitupDocument) {
      const onConfirm = async () => {
        const deleted = await DocumentS.deleteDocument(bookitupDocument);
        if (deleted) {
          reload();
          return true;
        }
        return false;
      };
      openDeleteConfirmModal({
        title: i18n._('common.delete.document.confirm.head'),
        description: <Text>{i18n._('common.delete.document.confirm')}</Text>,
        onConfirm,
      });
    }
  };

  const downloadDocument = () => {
    downloadDocumentContractQuestionnaire('documents', id, i18n, () => setDraft(false));
  };

  const markAsReady = () => {
    patchDocument({ draft: false }, () =>
      ToastS.success('document-finished', 'Dokument wurde erfolgreich abgeschlossen'),
    );
  };

  const markAsAccepted = () => {
    patchDocument({ accepted: true }, () => ToastS.success('document-accepted', 'Dokument als akzeptiert markiert.'));
  };

  function copy(documentType: any, templateName: any, customerId: any) {
    setLoading(true);
    DocumentS.copyDocument(id, documentType, templateName, customerId, undefined, interceptViolation)
      .then((newDocument) => {
        if (newDocument)
          navigate(`${newDocument.eventId ? `/events/${newDocument.eventId}` : ''}/documents/${newDocument.id}`);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const createDocument = (initialDocumentType: BookitupDocumentType, finalInvoice?: boolean) => {
    openModal({
      modalId: 'create-document-modal',
      size: 'sm',
      title: (
        <Text weight="bolder" size="xl" sx={{ lineHeight: 1.2 }}>
          Vorlage wählen
        </Text>
      ),
      closeOnEscape: true,
      transition: 'slide-down',
      children: (
        <CreateDocumentModal
          copyingDocument
          documentId={id}
          initialDocumentType={initialDocumentType}
          initialCustomerId={customerId}
          eventParticipationContext={eventParticipantsContext}
          eventId={eventId}
          finalInvoice={finalInvoice}
          openLicenceModal={openLicenceModal}
          interceptViolation={interceptViolation}
        />
      ),
    });
  };

  const createNextReminder = () => {
    openCreateDocumentModal({
      copyingDocument: true,
      documentId: originDocumentId,
      initialDocumentType: 'REMINDER',
      eventParticipationContext: eventParticipantsContext,
      openLicenceModal,
      interceptViolation,
      eventId,
      initialCustomerId: customerId,
    });
  };

  const createContract = () => {
    if (!eventId) {
      ToastS.warn('foo', 'Das Dokument muss zuerst einem Event zugeordnet werden.');
      return;
    }
    openCreateContractModal({
      customerId: bookitupDocument.customerId,
      eventId: eventId ?? undefined,
      documentId: bookitupDocument.id,
    });
  };

  const createDownPayment = () => {
    executeWithLicenceCheck(LicenceS.Restrictions.SAVE_PARTIAL_INVOICE, () => {
      openModal({
        size: 'sm',
        title: (
          <Text mt="md" weight="bolder" size="xl" align="center" sx={{ lineHeight: 1.2 }}>
            Abschlagsrechnung
          </Text>
        ),
        closeOnEscape: true,
        transition: 'slide-down',
        children: (
          <CreateDownPaymentModal
            documentId={id}
            initialCustomerId={customerId}
            eventParticipationContext={eventParticipantsContext}
          />
        ),
      });
    });
  };

  const managePayments = () => {
    executeWithLicenceCheck(LicenceS.Restrictions.DOCUMENT_PAYMENTS, () => {
      const modalTitle = `Zahlung(en) zu ${documentNumber} verwalten`;
      openModal({
        size: 'md',
        title: (
          <Text weight="bolder" size="xl">
            {modalTitle}
          </Text>
        ),
        closeOnEscape: true,
        onClose: refresh,
        transition: 'slide-down',
        children: <PaymentsManagerModal bookitupDocument={bookitupDocument} interceptViolation={interceptViolation} />,
      });
    });
  };

  return (
    <div>
      <Menu withinPortal shadow="md" width={240} transition="scale-y" position="bottom-end">
        <Menu.Target>
          <ActionIcon>
            <IconDots data-testid={TestS.Documents.List.Dropdown.OPEN} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          {draft && (
            <>
              <Menu.Item
                icon={<IconCheck size={16} />}
                onClick={markAsReady}
                data-testid={TestS.Documents.List.Dropdown.NON_DRAFT}
              >
                Fertigstellen
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          {!toolMissing && DocumentS.isInvoice(documentType) && (!syncedAt || !isUptodate) && (
            <Menu.Item onClick={sync} icon={<IconSend size={16} />}>
              {syncedAt === null ? 'an Buchhaltung senden' : 'Rechnung aktualisieren'}
            </Menu.Item>
          )}
          {!draft && documentType === 'OFFER' && !acceptedAt && (
            <>
              <Menu.Item
                onClick={markAsAccepted}
                icon={<IconCheck size={16} />}
                data-testid={TestS.Documents.List.Dropdown.ACCEPT}
              >
                Angebot annehmen
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          {acceptedAt && (
            <>
              <Menu.Item
                onClick={() => patchDocument({ accepted: false })}
                icon={<IconX size={16} />}
                data-testid={TestS.Documents.List.Dropdown.ACCEPT}
              >
                Angebot widerrufen
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          {!draft && DocumentS.contractCreatable(documentType) && (
            <Menu.Item onClick={() => executeWithLicenceCheck(LicenceS.Restrictions.CONTRACT_USAGE, createContract)}>
              Vertrag erstellen
            </Menu.Item>
          )}
          {DocumentS.isInvoice(documentType) && (
            <>
              <Menu.Item
                onClick={() => createDocument('CANCEL')}
                data-testid={TestS.Documents.List.Dropdown.CREATE_CANCEL}
              >
                Rechnungskorrektur erstellen
              </Menu.Item>
              {!paid && (
                <Menu.Item
                  onClick={() => createDocument('REMINDER')}
                  data-testid={TestS.Documents.List.Dropdown.CREATE_REMINDER}
                >
                  Mahnung erstellen
                </Menu.Item>
              )}
              <Menu.Divider />
            </>
          )}
          {(documentType === 'CONFIRMATION' ||
            documentType === 'OFFER' ||
            (documentType === 'PARTIAL_INVOICE' && finalInvoiceCreatable)) && (
            <>
              {documentType === 'OFFER' && (
                <Menu.Item
                  onClick={() => createDocument('CONFIRMATION')}
                  data-testid={TestS.Documents.List.Dropdown.CREATE_CONFIRMATION}
                >
                  Auftragsbestätigung erstellen
                </Menu.Item>
              )}
              {documentType !== 'PARTIAL_INVOICE' && (
                <>
                  <Menu.Item
                    onClick={createDownPayment}
                    data-testid={TestS.Documents.List.Dropdown.CREATE_PARTIAL_INVOICE}
                  >
                    Abschlagsrechnung erstellen
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => createDocument('INVOICE')}
                    data-testid={TestS.Documents.List.Dropdown.CREATE_INVOICE}
                  >
                    Rechnung erstellen
                  </Menu.Item>
                </>
              )}
              {finalInvoiceCreatable && (
                <Menu.Item
                  onClick={() => createDocument('INVOICE', true)}
                  data-testid={TestS.Documents.List.Dropdown.CREATE_FINAL_INVOICE}
                >
                  Schlussrechnung erstellen
                </Menu.Item>
              )}
              <Menu.Divider />
            </>
          )}

          {(documentType === 'INVOICE' || documentType === 'PARTIAL_INVOICE') && !draft && (
            <Menu.Item
              icon={<IconCash size={16} />}
              onClick={managePayments}
              data-testid={TestS.Documents.List.Dropdown.MANAGE_PAYMENTS}
            >
              Zahlungen verwalten
            </Menu.Item>
          )}
          <Menu.Item
            icon={<IconCalendarPlus size={16} />}
            onClick={() => openAssignEventModal({ bookitupDocument, onChange: reload })}
          >
            Event zuordnen
          </Menu.Item>
          <Menu.Item
            icon={<IconDownload size={16} />}
            onClick={downloadDocument}
            data-testid={TestS.Documents.List.Dropdown.DOWNLOAD}
          >
            Herunterladen
          </Menu.Item>
          {['OFFER', 'INVOICE', 'CONFIRMATION'].includes(documentType) && (
            <Menu.Item
              icon={<IconCopy size={16} />}
              onClick={() => copy(undefined, undefined, undefined)}
              data-testid={TestS.Documents.List.Dropdown.DUPLICATE}
            >
              Duplizieren
            </Menu.Item>
          )}
          {documentType === 'REMINDER' && <Menu.Item onClick={createNextReminder}>Nächste Mahnung erstellen</Menu.Item>}
          <Menu.Item
            icon={<IconPencil size={16} />}
            onClick={toggleConfirm}
            data-testid={TestS.Documents.List.Dropdown.RENAME}
          >
            Umbenennen
          </Menu.Item>
          <Menu.Item
            color="red"
            icon={<IconTrash size={16} />}
            onClick={deleteHandler}
            data-testid={TestS.Documents.List.Dropdown.DELETE}
          >
            Löschen
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      {showConfirm && (
        <ChangeLabel
          entityType="documents"
          toggle={toggleConfirm}
          initialLabel={label}
          entityId={id}
          handleChange={handleChange}
        />
      )}
    </div>
  );
};

export default DocumentItemActions;
